import React, { FC, ReactElement } from 'react';
// Components
import Text from '../text';
import ModalButton, { ButtonType } from './templates/modalButton';
import ModalButtonContainer from './templates/modalButtonContainer';
// Resources
import { rps, stringsRes } from '../../resources/strings';
import { PropsFromModal } from './createModalHOC';

/**
 * LT-1884 — ce que le passager voit quand billing n'a pas pu garantir le
 * paiement d'une course.
 *
 * Un seul composant pour trois etats : deux decrivent UNE course a deux
 * moments de sa vie, le troisieme (AGGREGATE) parle du lot. C'est le meme
 * sujet vu de deux distances, pas deux composants.
 *
 * RIEN ne s'affiche tant que la validation suit son cours : elle doit
 * paraitre acquise (operateur, 17/09). Seuls le refus et l'annulation
 * parlent. Une reservation prise dix jours a l'avance attend d'ailleurs une
 * semaine avant sa premiere tentative (LT-1930) — annoncer une verification
 * en cours pendant tout ce temps n'informerait de rien et inquieterait.
 *
 * L'etat est decide par l'appelant, qui lit `payment_clearance_status` sur la
 * course (helpers de business.ts) — la modale ne connait pas le modele et ne
 * va rien chercher. Chaque etat propose une action : une carte valide relance
 * la validation toute seule, sans autre geste.
 */
export enum PaymentClearanceState {
    /** Paiement refuse, course encore vivante. Le passager peut la sauver. */
    BLOCKED = 'BLOCKED',
    /** Course deja annulee faute de paiement. Il n'y a plus rien a sauver. */
    CANCELLED = 'CANCELLED',
    /**
     * Niveau 1 : l'alerte d'ensemble, une ou plusieurs courses a regulariser.
     * Ne parle d'aucune course en particulier — elle renvoie vers le moyen de
     * paiement, qui corrige tout le lot d'un coup puisque c'est la meme carte.
     */
    AGGREGATE = 'AGGREGATE',
}

export interface PaymentClearanceModalProps extends PropsFromModal {
    state: PaymentClearanceState;
    /** Ouvre l'ecran de moyen de paiement. Requis sur BLOCKED, ignore ailleurs. */
    onChangePaymentMethod?: () => void;
    /** Relance une reservation. Requis sur CANCELLED, ignore ailleurs. */
    onBookAgain?: () => void;
    /** Nombre de courses a regulariser. Requis sur AGGREGATE, ignore ailleurs. */
    count?: number;
}

const PaymentClearance: FC<PaymentClearanceModalProps> = ({
    state,
    onChangePaymentMethod,
    onBookAgain,
    count = 0,
    onRequestClose,
}): ReactElement => {
    const res = stringsRes.paymentClearance;

    const content = {
        [PaymentClearanceState.BLOCKED]: {
            description: res.blockedDescription,
            cta: { id: 'changePaymentMethod', title: res.blockedCta, onClick: onChangePaymentMethod },
            closeLabel: res.close,
        },
        [PaymentClearanceState.CANCELLED]: {
            description: res.cancelledDescription,
            cta: { id: 'bookAgain', title: res.cancelledCta, onClick: onBookAgain },
            closeLabel: res.close,
        },
        [PaymentClearanceState.AGGREGATE]: {
            description: count > 1 ? rps(res.alertDescriptionMany, { count }) : res.alertDescriptionOne,
            cta: { id: 'fixPaymentMethod', title: res.blockedCta, onClick: onChangePaymentMethod },
            closeLabel: res.close,
        },
    }[state];

    // Les boutons de modale du depot (ModalButton), pas TextCenterButton : la
    // version precedente rendait « Fermer » en contour fin, presque invisible a
    // cote de l'action pleine (retour operateur, 17/09). CANCEL est le
    // secondaire de toutes les autres modales — fond gris, texte sombre — donc
    // lisible, et coherent avec ce que le passager voit ailleurs.
    //
    // Empiles, pas cote a cote (operateur, 18/09) : l'action principale est une
    // phrase — « Changer de moyen de paiement » — et le plafond de 160 px de la
    // disposition en ligne la coupait. En colonne chaque bouton prend toute la
    // largeur de la modale et grandit si son libelle passe sur deux lignes ;
    // rien n'est jamais tronque, quelle que soit la langue ou l'ecran. Le
    // principal en premier, comme dans toute pile de boutons.
    return (
        <div>
            <Text.Description14 text={content.description} />

            <ModalButtonContainer column>
                {content.cta.onClick && (
                    <ModalButton
                        id={content.cta.id}
                        type={ButtonType.CONFIRM}
                        label={content.cta.title}
                        fullWidth
                        onClick={() => {
                            // Fermer d'abord : l'action navigue, et une modale laissee
                            // ouverte derriere la navigation reste affichee sur l'ecran
                            // d'arrivee.
                            onRequestClose();
                            content.cta.onClick?.();
                        }}
                    />
                )}
                <ModalButton
                    id="closePaymentClearance"
                    type={ButtonType.CANCEL}
                    label={content.closeLabel}
                    fullWidth
                    onClick={onRequestClose}
                />
            </ModalButtonContainer>
        </div>
    );
};

export default PaymentClearance;
